import { useState, useRef, useCallback } from 'react';
import _ from 'lodash';
import { type Board } from '@gorilla/common/src/lib/monday-api/api';
import { type Theme } from '@gorilla/portal-shared/src/schemas';
import { useBufferState } from '../hooks/use-buffer-state';
import {
  Button,
  Divider,
  Flex,
  TextField,
  Text,
  Dropdown,
  Box,
  Icon,
  Loader,
  Label,
  Toggle,
} from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { Heading } from '@vibe/core';
import { HighlightColorBucket, Layout, Image, TextFormatting, Folder } from '@vibe/icons';
import { ColorSetting } from './ColorSetting';
import { Logo } from './Logo';
import { ThemePreview } from './ThemePreview';

const FONT_FAMILY_OPTIONS = [
  {
    value: 'barlow',
    label: 'Barlow',
    link: 'https://fonts.google.com/specimen/Barlow',
  },
  {
    value: 'figtree',
    label: 'Figtree',
    link: 'https://fonts.google.com/specimen/Figtree',
  },
  {
    value: 'karla',
    label: 'Karla',
    link: 'https://fonts.google.com/specimen/Karla',
  },
  {
    value: 'montserrat',
    label: 'Montserrat',
    link: 'https://fonts.google.com/specimen/Montserrat',
  },
  {
    value: 'mulish',
    label: 'Mulish',
    link: 'https://fonts.google.com/specimen/Mulish',
  },
  {
    value: 'nunito',
    label: 'Nunito Sans',
    link: 'https://fonts.google.com/specimen/Nunito+Sans',
  },
  {
    value: 'poppins',
    label: 'Poppins',
    link: 'https://fonts.google.com/specimen/Poppins',
  },
  {
    value: 'raleway',
    label: 'Raleway',
    link: 'https://fonts.google.com/specimen/Raleway',
  },
  {
    value: 'rubik',
    label: 'Rubik',
    link: 'https://fonts.google.com/specimen/Rubik',
  },
  {
    value: 'work-sans',
    label: 'Work Sans',
    link: 'https://fonts.google.com/specimen/Work+Sans',
  },
];

const COLOR_PRESETS = [
  {
    value: 'default',
    label: 'Default',
    colors: {
      primaryColor: '#465464',
      secondaryColor: '#fcfefe',
      teritaryColor: '#465464',
      specialColor: '#e3eaf1',
    },
  },
  {
    value: 'mulberry',
    label: 'Mulberry',
    colors: {
      primaryColor: '#574663',
      secondaryColor: '#fcfefe',
      teritaryColor: '#574663',
      specialColor: '#f3e6eb',
    },
  },
  {
    value: 'teal',
    label: 'Teal',
    colors: {
      primaryColor: '#2f5d62',
      secondaryColor: '#fcfefe',
      teritaryColor: '#2f5d62',
      specialColor: '#e6f2f2',
    },
  },
  {
    value: 'violet',
    label: 'Violet',
    colors: {
      primaryColor: '#494c9e',
      secondaryColor: '#fcfefe',
      teritaryColor: '#494c9e',
      specialColor: '#ececff',
    },
  },
];

type ThemeSettingsProps = {
  themeSettings: Theme;
  onChange: (newThemeSettings: Theme) => void;
  isUploadingLogo: boolean;
  clientBoard: Board;
  onLogoUpload: (uploadSettings: { name: string; content: string }) => Promise<any>;
  screenMode: 'small' | 'medium' | 'large';
  hasUnpublishedChanges?: boolean;
};

export function ThemeSettings({
  themeSettings: _themeSettings,
  onChange,
  isUploadingLogo,
  clientBoard,
  onLogoUpload,
  screenMode,
  hasUnpublishedChanges,
}: ThemeSettingsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { bufferState, setBufferState } = useBufferState<Theme>(onChange, 700);
  const currentTheme = bufferState || _themeSettings;

  let selectedFontFamily = FONT_FAMILY_OPTIONS.find((option) => option.value === currentTheme.fontFamily) || {
    value: 'poppins',
    label: 'Poppins',
  };

  const labelRendererFontFamily = useCallback(({ value, label, link }: { value: any; label: any; link?: any }) => {
    return (
      <Flex gap={8} style={{ width: '100%' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontFamily: `"${label} Picker", sans-serif`,
            fontWeight: 400,
          }}
        >
          {label}
        </div>
      </Flex>
    );
  }, []);

  let logo = null;

  if (isUploadingLogo) {
    // @ts-ignore
    logo = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          fontSize: '14px',
          color: currentTheme.secondaryColor,
        }}
      >
        <Loader color="dark" size={15} />
      </div>
    );
  } else if (currentTheme.logo) {
    // @ts-ignore
    logo = <>{clientBoard ? <Logo fileHash={currentTheme.logo} boardId={clientBoard.id} position="center" /> : null}</>;
  } else {
    // @ts-ignore
    logo = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 14px',
          width: '100%',
          height: '100%',
          borderRadius: '4px',
          fontSize: '16px',
          backgroundColor: '#fff',
          color: '#32333861',
        }}
      >
        <Flex justify="start">&nbsp;</Flex>
      </div>
    );
  }

  return (
    <div
      style={{
        overflow: 'hidden',
        overflowY: 'auto',
        height: '100%',
        maxHeight: 'calc(100vh - 73px)',
      }}
    >
      <Box
        margin="large"
        style={{
          overflow: 'visible',
        }}
      >
        <Flex
          gap={24}
          direction="column"
          style={{
            width: '100%',
          }}
        >
          <Flex
            gap={24}
            direction={screenMode === 'small' ? 'column' : 'row'}
            align="stretch"
            style={{
              width: 'inherit',
            }}
          >
            <Flex
              direction="column"
              style={{
                width: screenMode === 'small' ? '100%' : '370px',
                minWidth: screenMode === 'small' ? '100%' : '370px',
                maxWidth: screenMode === 'small' ? '100%' : '370px',
              }}
            >
              <Box
                backgroundColor="primaryBackgroundColor"
                borderColor="uiBorderColor"
                border
                rounded="small"
                shadow="xs"
                style={{
                  width: '100%',
                }}
              >
                <Box
                  style={{
                    width: '100%',
                  }}
                >
                  <Flex
                    style={{
                      flexDirection: screenMode === 'small' ? 'row' : 'column',
                      justifyContent: 'start',
                      alignItems: screenMode === 'small' ? 'center' : 'flex-start',
                      width: '100%',
                    }}
                  >
                    {screenMode !== 'small' ? (
                      <>
                        <Box padding="xl" paddingTop="large">
                          <Flex direction="column" justify="start" align="start" gap={16}>
                            <Flex gap={10} style={{ width: '100%' }}>
                              <Icon icon={Layout} size={22} />
                              <Heading type="h3" weight="bold" align="start">
                                Layout
                              </Heading>
                              <Box style={{ marginLeft: 'auto' }}></Box>
                            </Flex>
                            <ThemePreview themeSettings={currentTheme} clientBoard={clientBoard} screenMode={screenMode} />
                          </Flex>
                        </Box>

                        <div style={{ alignSelf: 'stretch' }}>
                          <Divider withoutMargin />
                        </div>
                      </>
                    ) : null}

                    <Flex gap={0} direction="column" justify="start" align="start" style={{ width: '100%' }}>
                      <Box
                        padding="xl"
                        paddingTop="large"
                        paddingBottom="large"
                        style={{
                          width: 'calc(100% - 64px)',
                        }}
                      >
                        <Flex direction="column" justify="start" align="start" gap={16}>
                          <Flex gap={10} style={{ width: '100%' }}>
                            <Icon icon={TextFormatting} size={26} />
                            <Heading type="h3" weight="bold" align="start">
                              Font
                            </Heading>
                            <Box style={{ marginLeft: 'auto' }}>
                              <Button
                                kind="tertiary"
                                size="xs"
                                onClick={() => {
                                  setBufferState({ ...currentTheme, fontFamily: 'poppins' });
                                }}
                                style={{
                                  padding: '10px 8px',
                                }}
                                disabled={currentTheme.fontFamily === 'poppins'}
                              >
                                Reset to default
                              </Button>
                            </Box>
                          </Flex>
                          <Text type="text2" color="primary" align="start" ellipsis={false}>
                            Choose a Google Webfont from the dropdown.
                          </Text>
                          <Box style={{ zIndex: 300, position: 'relative', width: '100%' }}>
                            <Dropdown
                              placeholder="Choose font"
                              size="medium"
                              clearable={false}
                              searchable={true}
                              options={FONT_FAMILY_OPTIONS}
                              value={dropdownValue(selectedFontFamily)}
                              onChange={(option: any) => {
                                setBufferState({ ...currentTheme, fontFamily: option.value });
                              }}
                              className="custom_dropdown"
                              optionRenderer={labelRendererFontFamily}
                            />
                          </Box>
                        </Flex>
                      </Box>
                    </Flex>
                  </Flex>

                  <Divider withoutMargin />

                  <Flex gap={0} direction="column" justify="start" align="start">
                    <Box
                      padding="xl"
                      paddingTop="large"
                      paddingBottom="large"
                      backgroundColor="greyBackgroundColor"
                      style={{
                        width: 'calc(100% - 64px)',
                      }}
                    >
                      <Flex direction="column" justify="start" align="start" gap={16}>
                        <Flex gap={10} style={{ width: '100%' }}>
                          <Icon icon={Image} size={24} style={{ transform: 'translate3d(-1px,0,0)' }} />
                          <Heading type="h3" weight="bold" align="start">
                            Logo
                          </Heading>
                          <Box style={{ marginLeft: 'auto' }}>
                            <Button
                              kind="tertiary"
                              size="xs"
                              onClick={() => {
                                setBufferState(_.omit(currentTheme, ['logo']));
                              }}
                              style={{
                                padding: '10px 8px',
                              }}
                              disabled={!currentTheme.logo}
                            >
                              Reset to default
                            </Button>
                          </Box>
                        </Flex>
                        <Text type="text2" color="primary" align="start" ellipsis={false}>
                          PNG or SVG recommended at below 100 KB.
                        </Text>
                        <Box
                          style={{
                            overflow: 'visible',
                            flex: 1,
                            width: '100%',
                          }}
                        >
                          <Flex direction="row" justify="start" align="center" gap={16}>
                            <form
                              className="filepicker filepicker--themesettings"
                              style={{ position: 'relative', flex: 1, width: 'auto', height: '42px' }}
                            >
                              <div
                                className="input_container"
                                style={{
                                  pointerEvents: currentTheme.logo || isUploadingLogo ? 'none' : 'auto',
                                  opacity: currentTheme.logo || isUploadingLogo ? 0 : 1,
                                }}
                              >
                                <input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.svg,.webp"
                                  id="fileUpload"
                                  ref={fileInputRef}
                                  onChange={() => {
                                    if (fileInputRef.current?.files?.length) {
                                      const file = fileInputRef.current.files[0];
                                      const filename = file.name;
                                      const reader = new FileReader();

                                      reader.onload = async (e) => {
                                        const fileSize = file.size;

                                        // only accept images up to 100kb for now
                                        if (fileSize > 100000) {
                                          // reset file input
                                          if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                          }
                                          //alert('The file size is too big, please upload an image that is less than 100 KB.');
                                          setErrorMessage('File size is too large.');
                                          return;
                                        }

                                        // if it's not an image, don't allow it
                                        if (!file.type.startsWith('image/')) {
                                          if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                          }

                                          //alert('Invalid file type, please upload an image.');
                                          setErrorMessage('File type is invalid.');
                                          return;
                                        }

                                        setErrorMessage('');
                                        const contentBase64 = e.target?.result as string;
                                        try {
                                          const hash = await onLogoUpload({ name: filename, content: contentBase64 });
                                          setBufferState({
                                            ...currentTheme,
                                            logo: hash,
                                          });
                                        } catch (err) {}
                                      };

                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </div>

                              <Box
                                style={{
                                  pointerEvents: currentTheme.logo ? 'auto' : 'none',
                                  overflow: 'visible',
                                  display: 'flex',
                                  justifyItems: 'center',
                                  alignItems: 'center',
                                  zIndex: 100,
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '42px',
                                  borderRadius: '4px',
                                  backgroundColor: currentTheme.logo || isUploadingLogo ? currentTheme.primaryColor : '#f6f7fb',
                                  lineHeight: 0,
                                  opacity: currentTheme.logo || isUploadingLogo ? 1 : 0,
                                }}
                                className={currentTheme.logo ? 'portal__logo__haslogo' : 'portal__logo__nologo'}
                              >
                                {isUploadingLogo ? (
                                  <div style={{ margin: '0 auto' }}>
                                    <Loader color="onPrimary" size={15} />
                                  </div>
                                ) : (
                                  <>{logo}</>
                                )}
                              </Box>
                              {errorMessage !== '' && (
                                <Box
                                  style={{
                                    display: 'flex',
                                    justifyItems: 'center',
                                    alignItems: 'center',
                                    zIndex: 100,
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: '140px',
                                    width: '100%',
                                    height: '42px',
                                    borderRadius: '4px',
                                    backgroundColor: '#f6f7fb',
                                    lineHeight: 0,
                                  }}
                                >
                                  <Text type="text2" color="secondary" align="start">
                                    {errorMessage}
                                  </Text>
                                </Box>
                              )}
                            </form>
                          </Flex>
                        </Box>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Box>
            </Flex>

            <Box
              style={{
                overflow: 'visible',
                flex: 1,
                width: '100%',
              }}
            >
              <Flex
                direction="column"
                justify="start"
                align="start"
                gap={24}
                style={{
                  width: 'inherit',
                }}
              >
                <Box
                  paddingTop="xl"
                  border={true}
                  borderColor="uiBorderColor"
                  rounded="small"
                  backgroundColor="primaryBackgroundColor"
                  shadow="xs"
                  style={{
                    overflow: 'visible',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Flex direction="column" justify="start" align="start" gap={16}>
                    <Box paddingEnd="xl" paddingStart="xl" style={{ width: 'calc(100% - 64px)' }}>
                      <Flex
                        gap={16}
                        style={{
                          width: '100%',
                        }}
                      >
                        {COLOR_PRESETS.map((preset, index) => (
                          <Flex
                            key={index}
                            direction="row"
                            gap={16}
                            style={{
                              flex: 1,
                              width: 'inherit',
                            }}
                          >
                            <Box
                              style={{
                                width: '100%',
                              }}
                            >
                              <div
                                className="theme__picker__item"
                                onClick={() => {
                                  setBufferState({
                                    ...currentTheme,
                                    ...preset.colors,
                                  });
                                }}
                              >
                                <div className="theme__picker__item--colors">
                                  <div
                                    className="theme__picker__item--colorblock"
                                    style={{ backgroundColor: preset.colors.primaryColor }}
                                  ></div>
                                  <div
                                    className="theme__picker__item--colorblock"
                                    style={{ backgroundColor: preset.colors.specialColor }}
                                  ></div>
                                  <div
                                    className="theme__picker__item--colorblock"
                                    style={{ backgroundColor: preset.colors.secondaryColor }}
                                  ></div>
                                </div>
                              </div>
                            </Box>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>

                    <Box
                      paddingTop="medium"
                      paddingStart="xl"
                      paddingEnd="xl"
                      style={{
                        width: 'calc(100% - 64px)',
                      }}
                    >
                      <Flex gap={16} style={{ width: '100%' }}>
                        <Flex gap={6}>
                          <Icon icon={HighlightColorBucket} size={24} style={{ transform: 'translate3d(0,-1px,0)' }} />
                          <Heading type="h3" weight="bold" align="start">
                            Colors
                          </Heading>
                        </Flex>
                        <Box style={{ marginLeft: 'auto' }}>
                          <Button
                            kind="tertiary"
                            size="xs"
                            onClick={() => {
                              setBufferState({
                                ...currentTheme,
                                primaryColor: '#465464',
                                secondaryColor: '#fcfefe',
                                teritaryColor: '#465464',
                                specialColor: '#e3eaf1',
                                successColor: '#00c875',
                                warningColor: '#fdab3d',
                                errorColor: '#df2f4a',
                              });
                            }}
                            style={{
                              padding: '10px 8px',
                            }}
                          >
                            Reset to default
                          </Button>
                        </Box>
                      </Flex>
                    </Box>

                    <Box paddingEnd="xl" paddingBottom="xl" paddingStart="xl" style={{ width: 'calc(100% - 64px)' }}>
                      <Flex
                        direction="row"
                        justify="start"
                        align="start"
                        gap={32}
                        style={{
                          width: '100%',
                        }}
                      >
                        <Flex
                          direction={screenMode !== 'small' ? 'row' : 'column'}
                          justify="start"
                          align="start"
                          gap={screenMode !== 'small' ? 32 : 20}
                          style={{ width: '100%' }}
                        >
                          <Flex
                            direction={screenMode !== 'small' ? 'column' : 'row'}
                            justify="start"
                            align="start"
                            gap={screenMode !== 'small' ? 21 : 24}
                            style={{ width: '100%' }}
                          >
                            <div className="colorpicker__wrapper">
                              <ColorSetting
                                color={currentTheme.primaryColor}
                                label="Primary"
                                onChange={(newColor) => {
                                  setBufferState({ ...currentTheme, primaryColor: newColor });
                                }}
                              />
                            </div>
                            <div className="colorpicker__wrapper">
                              <ColorSetting
                                color={currentTheme.secondaryColor}
                                label="Secondary"
                                onChange={(newColor) => {
                                  setBufferState({ ...currentTheme, secondaryColor: newColor });
                                }}
                              />
                            </div>
                          </Flex>
                          <Flex
                            direction={screenMode !== 'small' ? 'column' : 'row'}
                            justify="start"
                            align="start"
                            gap={screenMode !== 'small' ? 21 : 24}
                            style={{ width: '100%' }}
                          >
                            <div className="colorpicker__wrapper">
                              <ColorSetting
                                color={currentTheme.teritaryColor}
                                label="Text"
                                onChange={(newColor) => {
                                  setBufferState({ ...currentTheme, teritaryColor: newColor });
                                }}
                              />
                            </div>
                            <div className="colorpicker__wrapper">
                              <ColorSetting
                                color={currentTheme.specialColor}
                                label="Surfaces"
                                onChange={(newColor) => {
                                  setBufferState({ ...currentTheme, specialColor: newColor });
                                }}
                              />
                            </div>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>

                  <Divider withoutMargin />

                  <Box
                    padding="xl"
                    paddingTop="large"
                    backgroundColor="greyBackgroundColor"
                    style={{
                      borderBottomRightRadius: '4px',
                      borderBottomLeftRadius: '4px',
                    }}
                  >
                    <Flex
                      direction={screenMode !== 'small' ? 'row' : 'row'}
                      justify="start"
                      align="start"
                      gap={screenMode !== 'small' ? 32 : 22}
                      style={{ width: '100%' }}
                    >
                      <div className="colorpicker__wrapper">
                        <ColorSetting
                          color={currentTheme.successColor || '#00c875'}
                          label="Success"
                          onChange={(newColor) => {
                            setBufferState({ ...currentTheme, successColor: newColor });
                          }}
                        />
                      </div>
                      <div className="colorpicker__wrapper">
                        <ColorSetting
                          color={currentTheme.warningColor || '#fdab3d'}
                          label="Warning"
                          onChange={(newColor) => {
                            setBufferState({ ...currentTheme, warningColor: newColor });
                          }}
                        />
                      </div>
                      <div className="colorpicker__wrapper">
                        <ColorSetting
                          color={currentTheme.errorColor || '#df2f4a'}
                          label="Error"
                          onChange={(newColor) => {
                            setBufferState({ ...currentTheme, errorColor: newColor });
                          }}
                        />
                      </div>
                    </Flex>
                  </Box>
                </Box>

                {/* Sidebar panel */}

                <Box
                  border={true}
                  borderColor="uiBorderColor"
                  rounded="small"
                  backgroundColor="primaryBackgroundColor"
                  shadow="xs"
                  style={{
                    overflow: 'visible',
                    width: '100%',
                  }}
                >
                  <Flex gap={0} direction="column" justify="start" align="start">
                    <Box
                      padding="xl"
                      paddingTop="large"
                      paddingBottom="large"
                      style={{
                        width: 'calc(100% - 64px)',
                      }}
                    >
                      <Flex direction="column" justify="start" align="start" gap={16}>
                        <Flex gap={10} style={{ width: '100%' }}>
                          <Icon icon={Folder} size={24} style={{ transform: 'rotate(90deg)' }} />
                          <Heading type="h3" weight="bold" align="start">
                            Collapse sidebar by default
                          </Heading>
                          <Box style={{ marginLeft: 'auto' }}>
                            <div
                              style={{
                                marginRight: '-8px',
                                marginLeft: 'auto',
                              }}
                            >
                              <Toggle
                                offOverrideText=""
                                onOverrideText=""
                                isSelected={!!currentTheme.sidebarCollapsedByDefault}
                                onChange={(value) => {
                                  setBufferState({ ...currentTheme, sidebarCollapsedByDefault: value });
                                }}
                                size="small"
                              />
                            </div>
                          </Box>
                        </Flex>
                        <Text type="text2" color="primary" align="start" ellipsis={false}>
                          Give content more space with a compact menu.
                        </Text>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </div>
  );
}
